import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { emit } from '../../platform/analytics/analytics'

/** `POST /transactions/bulk-delete` takes 1–500 ids per call. */
export const BULK_DELETE_MAX = 500

export function chunkIds(ids: readonly string[], size: number = BULK_DELETE_MAX): string[][] {
  const out: string[][] = []
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size))
  return out
}

/**
 * A delete that did not complete. `status` is the HTTP status, absent when the
 * request never reached the server. `deletedBefore` is how many rows EARLIER
 * chunks of the same run had already deleted (0 for a single call): a bulk 404
 * deletes nothing in its own chunk, so this is the only thing that can have
 * landed.
 */
export class DeleteTransactionsError extends Error {
  readonly status: number | undefined
  readonly deletedBefore: number
  /** The ids not yet deleted: the failed chunk and every one after it. Empty for a single call. */
  readonly remainingIds: readonly string[]
  constructor(cause: unknown, status: number | undefined, deletedBefore = 0, remainingIds: readonly string[] = []) {
    super('delete-transactions-failed', { cause })
    this.name = 'DeleteTransactionsError'
    this.status = status
    this.deletedBefore = deletedBefore
    this.remainingIds = remainingIds
  }
}

/**
 * Everything a deleted row was part of: the lists, detail, summary and review
 * count (all under `transactions`), Cashflow and Reports (under `cashflow`),
 * the review inbox, whose pending rows may include the deleted one, and the
 * budgets, accounts and recurring roots (budget `spent`, a manual account's
 * derived balance, and a recurring stream's linked payments).
 */
function invalidateAfterDelete(client: QueryClient, deletedId?: string): Promise<unknown> {
  // The deleted row's own detail query is dropped, not refetched: it would answer 404 to a screen that is
  // about to leave, and flash "no longer exists" on the way out.
  if (deletedId !== undefined) client.removeQueries({ queryKey: keys.transactions.detail(deletedId), exact: true })
  return Promise.all([
    client.invalidateQueries({ queryKey: keys.transactions.all() }),
    client.invalidateQueries({ queryKey: keys.cashflow.all() }),
    client.invalidateQueries({ queryKey: keys.review.all() }),
    client.invalidateQueries({ queryKey: keys.budgets.all() }),
    client.invalidateQueries({ queryKey: keys.accounts.all() }),
    client.invalidateQueries({ queryKey: keys.recurring.all() }),
  ])
}

/** `DELETE /transactions/{id}`: 204, and a repeat is 204 too. */
export function useDeleteTransaction() {
  const client = useQueryClient()
  return useMutation<void, DeleteTransactionsError, string>({
    networkMode: 'always',
    retry: false,
    mutationFn: async (id) => {
      let res
      try {
        res = await api.DELETE('/api/v1/transactions/{transaction_id}', { params: { path: { transaction_id: id } } })
      } catch (cause) {
        throw new DeleteTransactionsError(cause, undefined)
      }
      if (res.error !== undefined) throw new DeleteTransactionsError(res.error, res.response.status)
    },
    onSuccess: async (_, id) => {
      await invalidateAfterDelete(client, id)
      emit('transaction_deleted', { transactions_count: 1, scope: 'one' })
    },
  })
}

/**
 * Delete `ids` in chunks of 500, one request at a time. A chunk is all-or-
 * nothing on the server (any unknown id is a 404 and deletes none), but chunks
 * are independent, so a failure after the first leaves earlier chunks deleted:
 * the error says how many (and which ids remain), and the lists are refetched
 * either way. The UI caps a selection at 100 (BULK_CAP), so more than one chunk is
 * reached only through this hook, never from the toolbar.
 */
export function useDeleteTransactions() {
  const client = useQueryClient()
  return useMutation<{ deleted: number }, DeleteTransactionsError, readonly string[]>({
    networkMode: 'always',
    retry: false,
    mutationFn: async (ids) => {
      let deleted = 0
      const chunks = chunkIds(ids)
      for (const [i, chunk] of chunks.entries()) {
        const remaining = () => chunks.slice(i).flat()
        let res
        try {
          res = await api.POST('/api/v1/transactions/bulk-delete', { body: { transaction_ids: chunk } })
        } catch (cause) {
          throw new DeleteTransactionsError(cause, undefined, deleted, remaining())
        }
        if (res.error !== undefined || res.data === undefined) throw new DeleteTransactionsError(res.error, res.response.status, deleted, remaining())
        deleted += res.data.deleted
      }
      return { deleted }
    },
    onSuccess: async (out) => {
      await invalidateAfterDelete(client)
      emit('transaction_deleted', { transactions_count: out.deleted, scope: 'bulk' })
    },
    onError: async (error) => {
      if (error.deletedBefore > 0) {
        await invalidateAfterDelete(client)
        emit('transaction_deleted', { transactions_count: error.deletedBefore, scope: 'bulk' })
      }
    },
  })
}
